<!-- @format -->
<template>
    <div class="email-inbox-manage-widget">
        <div class="widget-container">
            <div class="widget-header">
                <h3>{{ widget.action }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="account-details">
                    <img
                        :class="[`${widget.accountInfo.providerCode}`]"
                        :src="widget.accountInfo.logo"
                        alt="email logo"
                    />
                    <div class="details">
                        <p>{{ widget.accountInfo.email }}</p>
                        <div
                            :class="[
                                { 'not-connected': !widget.accountInfo.isConnected },
                                'account-connected',
                            ]"
                        >
                            Connected
                            <div
                                :class="[
                                    { 'gray-background': !widget.accountInfo.isConnected },
                                    'status-circle',
                                ]"
                            ></div>
                        </div>
                        <p class="sync-date">
                            Last sync: {{ widget.accountInfo.lastSync | momentDateFromNow(false) }}
                        </p>
                    </div>
                </div>
                <div class="account-inputs">
                    <div class="input-container">
                        <label>Set as default email?</label>
                        <app-dropdown
                            :items="makeAccountDefaultOptions"
                            :value="defaultStatus"
                            @input="updateDefaultStatus"
                        />
                        <p class="input-helper-text">
                            If you have another email integration, this will determine which email
                            is the default whenever you try to email one of your contacts.
                        </p>
                    </div>

                    <div class="input-container">
                        <label>Enable/Disable Integration</label>
                        <app-switch v-model="enableIntegration" @input="toggleSwitch" />
                        <p class="input-helper-text">
                            You can disable this email without deleting it.
                        </p>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                        Close
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button class="remove-btn" @click="showDeleteIntegrationPopup">
                        Remove
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const AppDropdown = () => import('@/components/common/app-dropdown');
const AppSwitch = () => import('@/components/common/app-switch');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

import { EmailInboxService } from '@/scripts/services/email-inbox/email-inbox-service';
import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus.js';
import { LogLevel } from "@/scripts/logger";

export default {
    name: 'email-inbox-manage-widget',

    components: {
        'app-button': AppButton,
        'app-dropdown': AppDropdown,
        'app-switch': AppSwitch,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            enableIntegration: null,
            defaultIntegration: null,
            initialState: {
                enableIntegration: null,
                defaultIntegration: null,
            },

            makeAccountDefaultOptions: [
                {
                    id: 1,
                    name: 'Yes',
                },
                {
                    id: 2,
                    name: 'No',
                },
            ],
        };
    },

    computed: {
        defaultStatus() {
            return this.widget.accountInfo.isDefault
                ? this.makeAccountDefaultOptions[0]
                : this.makeAccountDefaultOptions[1];
        },
    },

    created() {
        this.enableIntegration = this.widget.accountInfo.isEnabled;
        this.initialState.enableIntegration = this.enableIntegration;
        this.initialState.defaultIntegration = this.defaultIntegration;
    },

    methods: {
        closeWidget() {
            if (this.hasChanged()) {
                EventBus.$emit('update-cards');
            }

            EventBus.$emit('close-widget', this.widget);
        },

        toggleSwitch() {
            this.updateIntegrations();
        },

        hasChanged() {
            return (
                this.enableIntegration !== this.initialState.enableIntegration ||
                this.defaultIntegration !== this.initialState.defaultIntegration
            );
        },

        showDeleteIntegrationPopup(item) {
            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Are you sure you want to delete this integration?',
                    content: `<p>If you remove this integration, GlassHive won't be able to access to your inbox. Are you sure you want to remove <span style="color: #382153;font-weight:bold;">${this.widget.accountInfo.email}</span>?</p> `,
                    actions: {
                        confirm: {
                            label: 'Delete',
                            action: this.deleteIntegration,
                        },
                        cancel: {
                            label: 'Cancel',
                        },
                    },
                    args: [item],
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                }
            );
        },

        updateIntegrations() {
            EmailInboxService.updateAccount(this.widget.accountInfo.id, {
                isEnabled: this.enableIntegration,
            })
                .then(() => {
                    if (this.enableIntegration) {
                        this.$helpers.showSuccessNotification(
                            'You have turned on the O365 integration.'
                        );
                    } else {
                        this.$helpers.showFailNotification(
                            'You have turned off the O365 integration.',
                            { level: LogLevel.INFO }
                        );
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your account',
                        { error },
                    );
                });
        },

        updateDefaultIntegration() {
            EmailInboxService.updateDefaultAccount(this.widget.accountInfo.id, {
                isDefault: this.defaultIntegration,
            }).catch((error) => {
                this.$helpers.showFailNotification('An error occurred while updating your account', { error });
            });
        },

        removeIntegrations() {
            EmailInboxService.removeAccount(this.widget.accountInfo.id)
                .then(() => {
                    EventBus.$emit('update-cards');
                    this.$helpers.showSuccessNotification(
                        `Your ${this.widget.accountInfo.domain} Integration was deleted.`
                    );
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while deleting this account',
                        { error },
                    );
                });
        },

        deleteIntegration() {
            this.removeIntegrations();
            this.closeWidget();
        },

        updateDefaultStatus(item) {
            if (item.name === 'Yes') {
                this.defaultIntegration = true;
            } else if (item.name === 'No') {
                this.defaultIntegration = false;
            }

            this.updateDefaultIntegration();
        },
    },
};
</script>

<style>
.email-inbox-manage-widget.widget.purple-gradient-header .widget-header {
    background: linear-gradient(264deg, #e38dee, #7a5bff);
}

.email-inbox-manage-widget .widget-container .content-wrapper {
    padding-top: 20px;
}

.email-inbox-manage-widget .widget-container {
    width: 100%;
}

.email-inbox-manage-widget .widget-container .transparent-btn {
    background: transparent;
    color: #877d88;
    border: 1px solid #cac6cb;
    border-radius: 20px;
    width: 120px;
}

.email-inbox-manage-widget .widget-container .remove-btn {
    background: #f30070 0% 0% no-repeat padding-box;
    border-radius: 20px;
    color: #ffffff;
    height: 40px;
    width: 126px;
}

.email-inbox-manage-widget .content-wrapper .account-details {
    background: #ffffff 0% 0% no-repeat padding-box;
    border: 2px solid #e9e9ef;
    border-radius: 15px;
    display: flex;
    height: 112.5px;
    padding: 14px 20px;
}

.email-inbox-manage-widget .content-wrapper .account-details .google {
    height: 84px;
    width: 112px;
}

.email-inbox-manage-widget .content-wrapper .account-details .microsoft {
    width: 68px;
    height: 68px;
    align-self: center;
    margin-right: 20px;
    margin-left: 23px;
}

.email-inbox-manage-widget .content-wrapper .account-details .details {
    display: flex;
    flex-direction: column;
    margin-left: 22px;
}

.email-inbox-manage-widget .content-wrapper .account-details .details {
    display: flex;
    flex-direction: column;
    margin-left: 22px;
}

.email-inbox-manage-widget .content-wrapper .account-details .details p {
    text-align: left;
    font: normal normal bold 16px/25px Roboto;
    letter-spacing: 0px;
    color: #382153;
    margin-bottom: 14.5px;
}

.email-inbox-manage-widget .content-wrapper .account-details .details .sync-date {
    text-align: left;
    font: normal normal normal 14px/14px Roboto;
    letter-spacing: 0px;
    color: #877d88;
}

.email-inbox-manage-widget .content-wrapper .account-details .details .account-connected {
    display: flex;
    align-items: center;
    text-align: right;
    font: normal normal normal 14px/14px Roboto;
    letter-spacing: 0px;
    color: #2ada2b;
    margin-bottom: 12.5px;
}

.email-inbox-manage-widget
    .content-wrapper
    .account-details
    .details
    .account-connected.not-connected {
    color: #877d88;
}

.email-inbox-manage-widget
    .content-wrapper
    .account-details
    .details
    .account-connected
    .status-circle {
    background-color: #2ada2b;
    border-radius: 50%;
    height: 12px;
    margin-left: 7px;
    width: 12px;
}

.email-inbox-manage-widget
    .content-wrapper
    .account-details
    .details
    .account-connected
    .status-circle.gray-background {
    background-color: #877d88;
}

.email-inbox-manage-widget .content-wrapper .account-inputs {
    display: flex;
    flex-direction: column;
    margin-top: 30px;
}

.email-inbox-manage-widget .content-wrapper .account-inputs .input-container {
    width: 100%;
}

.email-inbox-manage-widget .content-wrapper .account-inputs .input-container label {
    margin-bottom: 7px;
}

.email-inbox-manage-widget .content-wrapper .account-inputs .input-container .app-dropdown {
    height: 40px;
}

.email-inbox-manage-widget .content-wrapper .account-inputs .input-container .input-helper-text {
    text-align: left;
    font: normal normal normal 12px/14px Roboto;
    letter-spacing: 0px;
    color: #877d88;
}
</style>
